// The KPIs sidebar's seat tree (KPIS-MODE, founder-approved 2026-10-22): the org chart's shape —
// the top seat, its reports, theirs — cut to the seats whose KPIs the viewer may see
// (`kpi_visible_seat_ids`, the server's answer). A visible seat hangs under its nearest visible
// seat above; one with none above is a root, so a manager's branch starts at the manager. The
// open state (which nodes are expanded) is this browser's, per company.
import { type SeatNode, seatTree } from "../../features/chart/chart-model";

type TreeSeat = {
  id: string;
  title: string;
  supervisor_seat_id: string | null;
  status?: string;
};

/** The visible seats as a forest, each under its nearest visible ancestor (closed seats out). */
export function kpiSeatTree<S extends TreeSeat>(
  seats: readonly S[],
  visible: ReadonlySet<string>,
): SeatNode<S>[] {
  const byId = new Map(seats.map((s) => [s.id, s]));
  const shown = seats.filter((s) => visible.has(s.id) && s.status !== "closed");
  const shownIds = new Set(shown.map((s) => s.id));
  const sup = new Map<string, string | null>();
  for (const s of shown) {
    let cur = s.supervisor_seat_id;
    const seen = new Set<string>([s.id]);
    while (cur && !shownIds.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      cur = byId.get(cur)?.supervisor_seat_id ?? null;
    }
    sup.set(s.id, cur && shownIds.has(cur) ? cur : null);
  }
  return seatTree(shown, sup);
}

/**
 * The visible seats with their actual reporting chain (WORKFORCE): a seat between a visible
 * seat and its nearest visible ancestor stays in the tree as a structural seat (title and holder
 * only, its KPIs not shown), so Person ← Lead DE ← Junior DE never reads as Person ← Junior. A
 * visible seat with no visible ancestor is still a root (a manager's branch starts at the
 * manager); closed seats stay out. `structural` names the seats kept for shape only.
 */
export function kpiSeatChainTree<S extends TreeSeat>(
  seats: readonly S[],
  visible: ReadonlySet<string>,
): { forest: SeatNode<S>[]; structural: Set<string> } {
  const byId = new Map(seats.map((s) => [s.id, s]));
  const open = (id: string) => visible.has(id) && byId.get(id)?.status !== "closed";
  const structural = new Set<string>();
  for (const s of seats) {
    if (!open(s.id)) continue;
    const between: string[] = [];
    let cur = s.supervisor_seat_id;
    const seen = new Set<string>([s.id]);
    while (cur && byId.has(cur) && !open(cur) && !seen.has(cur)) {
      seen.add(cur);
      between.push(cur);
      cur = byId.get(cur)?.supervisor_seat_id ?? null;
    }
    // Only a chain that reaches a visible seat is kept; closed seats on it are skipped over.
    if (cur && open(cur) && !seen.has(cur))
      for (const id of between) if (byId.get(id)?.status !== "closed") structural.add(id);
  }
  const shown = seats.filter((s) => open(s.id) || structural.has(s.id));
  const shownIds = new Set(shown.map((s) => s.id));
  const sup = new Map<string, string | null>();
  for (const s of shown) {
    let cur = s.supervisor_seat_id;
    const seen = new Set<string>([s.id]);
    while (cur && !shownIds.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      cur = byId.get(cur)?.supervisor_seat_id ?? null;
    }
    sup.set(s.id, cur && shownIds.has(cur) ? cur : null);
  }
  return { forest: seatTree(shown, sup), structural };
}

/** A team in the SCOPE tree: its sub-teams, then the visible seats that are its own members. */
export type ScopeTeam<S extends TreeSeat> = {
  team: { id: string; name: string; parent_team_id: string | null };
  teams: ScopeTeam<S>[];
  seats: SeatNode<S>[];
};

/**
 * UX-A (ux pass 1.3): the KPIs sidebar's one SCOPE tree — Whole company › team › seat. Teams nest
 * under their parent teams (A–Z; a team whose parent is not listed is a root); under each team,
 * the reporting chain of the seats that are its own members (`kpiSeatChainTree`, cut to them);
 * `rest` is the chain of the visible seats in no team (the whole forest when there are no teams).
 * A seat in two teams shows under both: the tree answers "what can I scope to", not "where is it".
 */
export function kpiScopeTree<S extends TreeSeat>(
  seats: readonly S[],
  visible: ReadonlySet<string>,
  teams: readonly { id: string; name: string; parent_team_id: string | null }[],
  members: readonly { team_id: string; seat_id: string }[],
): { teams: ScopeTeam<S>[]; rest: SeatNode<S>[]; structural: Set<string> } {
  const structural = new Set<string>();
  const chain = (ids: ReadonlySet<string>) => {
    const t = kpiSeatChainTree(seats, ids);
    // Shape-only means "KPIs not shared with you", never "outside this team".
    for (const id of t.structural) if (!visible.has(id)) structural.add(id);
    return t.forest;
  };
  const own = new Map<string, Set<string>>();
  for (const m of members)
    if (visible.has(m.seat_id))
      own.set(m.team_id, (own.get(m.team_id) ?? new Set()).add(m.seat_id));
  const listed = new Set(teams.map((t) => t.id));
  const byParent = new Map<string | null, typeof teams>();
  for (const t of [...teams].sort((a, b) => a.name.localeCompare(b.name))) {
    const p = t.parent_team_id && listed.has(t.parent_team_id) ? t.parent_team_id : null;
    byParent.set(p, [...(byParent.get(p) ?? []), t]);
  }
  const seen = new Set<string>();
  const build = (parent: string | null): ScopeTeam<S>[] =>
    (byParent.get(parent) ?? [])
      .filter((t) => !seen.has(t.id) && seen.add(t.id))
      .map((t) => ({
        team: { id: t.id, name: t.name, parent_team_id: t.parent_team_id },
        teams: build(t.id),
        seats: chain(own.get(t.id) ?? new Set()),
      }));
  const tree = build(null);
  const teamed = new Set([...own.values()].flatMap((s) => [...s]));
  const rest = chain(new Set([...visible].filter((id) => !teamed.has(id))));
  return { teams: tree, rest, structural };
}

/** Every seat id in a forest (for the "expand all" default). */
export function treeIds<S extends { id: string; title: string }>(
  nodes: readonly SeatNode<S>[],
): string[] {
  return nodes.flatMap((n) => [n.seat.id, ...treeIds(n.children)]);
}

// UX-A: a new key for the SCOPE tree (its node ids include the scope rows), so a state stored by
// the old seat tree never opens it with Whole company collapsed.
const KEY = (companyId: string) => `companyos.kpis.scope.${companyId}`;

/** The expanded node ids remembered for the company (null: nothing stored yet). */
export function readTreeOpen(companyId: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(KEY(companyId));
    if (raw === null) return null;
    const list = JSON.parse(raw) as unknown;
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === "string") : null;
  } catch {
    return null;
  }
}

export function writeTreeOpen(companyId: string, ids: readonly string[]): void {
  try {
    window.localStorage.setItem(KEY(companyId), JSON.stringify(ids));
  } catch {
    // Storage blocked: the tree opens on its default next time.
  }
}
